/**
 * ADMIN PLANS & PRICING PAGE
 *
 * Manage subscription plans and pricing:
 * - Create/edit plans
 * - Configure features per plan
 * - Set regional pricing
 * - Manage free trials
 */

import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  CreditCard,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Users,
  FileText,
  Crown,
  Zap,
  Star,
  Building2,
  Globe,
  DollarSign,
} from 'lucide-react';

export const Route = createFileRoute('/admin/plans/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
    const profile = context.auth.profile;
    if (!profile?.isSuperAdmin) {
      throw redirect({ to: '/dashboard', replace: true });
    }
  },
  component: AdminPlansPage,
});

// Mock plans data
const mockPlans = [
  {
    id: '1',
    name: 'Starter',
    tierLevel: 1,
    maxUsers: 3,
    maxInvoicesPerMonth: 100,
    priceMonthlyDzd: 2500,
    priceYearlyDzd: 25000,
    isActive: true,
    description: 'Pour les petites entreprises qui démarrent',
    features: ['dashboard', 'products', 'clients', 'invoices', 'settings', 'multi_language', 'email_notifications'],
    companiesCount: 456,
    color: 'emerald',
  },
  {
    id: '2',
    name: 'Professional',
    tierLevel: 2,
    maxUsers: 10,
    maxInvoicesPerMonth: 500,
    priceMonthlyDzd: 7500,
    priceYearlyDzd: 75000,
    isActive: true,
    description: 'Pour les entreprises en croissance',
    features: ['dashboard', 'products', 'clients', 'invoices', 'settings', 'multi_language', 'email_notifications', 'pos', 'treasury', 'reports'],
    companiesCount: 289,
    color: 'blue',
  },
  {
    id: '3',
    name: 'Business',
    tierLevel: 3,
    maxUsers: 25,
    maxInvoicesPerMonth: 2000,
    priceMonthlyDzd: 15000,
    priceYearlyDzd: 150000,
    isActive: true,
    description: 'Pour les moyennes entreprises',
    features: ['dashboard', 'products', 'clients', 'invoices', 'settings', 'multi_language', 'email_notifications', 'pos', 'treasury', 'reports', 'checks', 'compliance', 'accounting', 'suppliers', 'offline_mode', 'multi_device'],
    companiesCount: 123,
    color: 'violet',
  },
  {
    id: '4',
    name: 'Enterprise',
    tierLevel: 4,
    maxUsers: -1, // Unlimited
    maxInvoicesPerMonth: -1, // Unlimited
    priceMonthlyDzd: 50000,
    priceYearlyDzd: 500000,
    isActive: true,
    description: 'Pour les grandes entreprises avec besoins personnalisés',
    features: ['all'], // All features
    companiesCount: 24,
    color: 'amber',
  },
];

const allFeatures = [
  { code: 'dashboard', name: 'Dashboard', free: true },
  { code: 'products', name: 'Products', free: true },
  { code: 'clients', name: 'Clients', free: true },
  { code: 'invoices', name: 'Invoices', free: true },
  { code: 'settings', name: 'Settings', free: true },
  { code: 'multi_language', name: 'Multi-Language', free: true },
  { code: 'email_notifications', name: 'Email Notifications', free: true },
  { code: 'pos', name: 'Point of Sale', free: false },
  { code: 'treasury', name: 'Treasury', free: false },
  { code: 'checks', name: 'Check Management', free: false },
  { code: 'compliance', name: 'Compliance', free: false },
  { code: 'accounting', name: 'Accounting', free: false },
  { code: 'reports', name: 'Reports', free: false },
  { code: 'suppliers', name: 'Suppliers', free: false },
  { code: 'offline_mode', name: 'Offline Mode', free: false },
  { code: 'multi_device', name: 'Multi-Device', free: false },
  { code: 'api_access', name: 'API Access', free: false },
  { code: 'custom_branding', name: 'Custom Branding', free: false },
];

const colorClasses: Record<string, { bg: string; text: string; border: string }> = {
  emerald: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  blue: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
  violet: { bg: 'bg-violet-500/20', text: 'text-violet-400', border: 'border-violet-500/30' },
  amber: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
};

function PlanCard({ plan, onEdit }: { plan: typeof mockPlans[0]; onEdit: () => void }) {
  const colors = colorClasses[plan.color];
  const yearlySavings = Math.round((1 - (plan.priceYearlyDzd / (plan.priceMonthlyDzd * 12))) * 100);

  return (
    <Card className={`bg-slate-800/50 border-slate-700 hover:border-violet-500/50 transition-colors ${plan.tierLevel === 4 ? 'ring-2 ring-amber-500/30' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className={`p-2 rounded-lg ${colors.bg}`}>
                {plan.tierLevel === 1 ? <Zap className={`w-5 h-5 ${colors.text}`} /> :
                 plan.tierLevel === 2 ? <Star className={`w-5 h-5 ${colors.text}`} /> :
                 plan.tierLevel === 3 ? <Crown className={`w-5 h-5 ${colors.text}`} /> :
                 <Building2 className={`w-5 h-5 ${colors.text}`} />}
              </div>
              <Badge className={`${colors.bg} ${colors.text} ${colors.border}`}>
                Tier {plan.tierLevel}
              </Badge>
              {!plan.isActive && (
                <Badge variant="secondary" className="bg-slate-700">Inactive</Badge>
              )}
            </div>
            <CardTitle className="text-white text-xl">{plan.name}</CardTitle>
            <CardDescription className="text-slate-400 mt-1">
              {plan.description}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" onClick={onEdit}>
            <Edit className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pricing */}
        <div className="p-4 rounded-lg bg-slate-700/30">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-white">
              {plan.priceMonthlyDzd.toLocaleString()}
            </span>
            <span className="text-slate-400">DZD/mois</span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-slate-400">
              {plan.priceYearlyDzd.toLocaleString()} DZD/an
            </span>
            {yearlySavings > 0 && (
              <Badge className="bg-emerald-500/20 text-emerald-400">
                -{yearlySavings}%
              </Badge>
            )}
          </div>
        </div>

        {/* Limits */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-slate-700/30">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Users</span>
            </div>
            <p className="text-lg font-semibold text-white mt-1">
              {plan.maxUsers === -1 ? 'Unlimited' : plan.maxUsers}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-slate-700/30">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Invoices/mo</span>
            </div>
            <p className="text-lg font-semibold text-white mt-1">
              {plan.maxInvoicesPerMonth === -1 ? 'Unlimited' : plan.maxInvoicesPerMonth.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Features count */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-700/30">
          <span className="text-slate-400">Features included</span>
          <span className="text-white font-medium">
            {plan.features.includes('all') ? 'All' : plan.features.length} features
          </span>
        </div>

        {/* Companies using */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-700">
          <span className="text-sm text-slate-400">Companies on this plan</span>
          <span className="text-white font-semibold">{plan.companiesCount.toLocaleString()}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function AdminPlansPage() {
  const [selectedTab, setSelectedTab] = useState('plans');
  const [editingPlan, setEditingPlan] = useState<typeof mockPlans[0] | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-violet-400" />
              Plans & Pricing
            </h1>
            <p className="text-slate-400 mt-2">
              Configure subscription plans and pricing for different regions
            </p>
          </div>
          <Button className="bg-violet-600 hover:bg-violet-700" onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Plan
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-500/20">
                  <CreditCard className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{mockPlans.length}</p>
                  <p className="text-sm text-slate-400">Total Plans</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <Building2 className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {mockPlans.reduce((sum, p) => sum + p.companiesCount, 0).toLocaleString()}
                  </p>
                  <p className="text-sm text-slate-400">Subscribed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <DollarSign className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">4.5M</p>
                  <p className="text-sm text-slate-400">MRR (DZD)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Globe className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">3</p>
                  <p className="text-sm text-slate-400">Regions</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="plans" className="data-[state=active]:bg-violet-600">Plans</TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-violet-600">Feature Matrix</TabsTrigger>
            <TabsTrigger value="regional" className="data-[state=active]:bg-violet-600">Regional Pricing</TabsTrigger>
            <TabsTrigger value="trials" className="data-[state=active]:bg-violet-600">Free Trials</TabsTrigger>
          </TabsList>

          <TabsContent value="plans" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mockPlans.map((plan) => (
                <PlanCard key={plan.id} plan={plan} onEdit={() => setEditingPlan(plan)} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="features" className="mt-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Feature Matrix</CardTitle>
                <CardDescription className="text-slate-400">
                  Compare features across all plans
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Feature</th>
                        {mockPlans.map((plan) => (
                          <th key={plan.id} className="text-center py-3 px-4">
                            <Badge className={`${colorClasses[plan.color].bg} ${colorClasses[plan.color].text}`}>
                              {plan.name}
                            </Badge>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allFeatures.map((feature) => (
                        <tr key={feature.code} className="border-b border-slate-700/50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className="text-white">{feature.name}</span>
                              {feature.free && (
                                <Badge variant="secondary" className="text-xs bg-slate-700">Free</Badge>
                              )}
                            </div>
                          </td>
                          {mockPlans.map((plan) => {
                            const hasFeature = plan.features.includes('all') || plan.features.includes(feature.code);
                            return (
                              <td key={plan.id} className="text-center py-3 px-4">
                                {hasFeature ? (
                                  <CheckCircle className="w-5 h-5 text-emerald-400 mx-auto" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-slate-600 mx-auto" />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="regional" className="mt-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Regional Pricing</CardTitle>
                <CardDescription className="text-slate-400">
                  Set different prices for different regions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Algeria */}
                  <div className="p-4 rounded-lg bg-slate-700/30">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-2xl">🇩🇿</span>
                      <div>
                        <h3 className="font-semibold text-white">Algeria (DZD)</h3>
                        <p className="text-sm text-slate-400">Primary market</p>
                      </div>
                      <Badge className="ml-auto bg-emerald-500/20 text-emerald-400">Active</Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                      {mockPlans.map((plan) => (
                        <div key={plan.id} className="text-center">
                          <p className="text-sm text-slate-400">{plan.name}</p>
                          <p className="text-lg font-semibold text-white">
                            {plan.priceMonthlyDzd.toLocaleString()} DZD
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* France */}
                  <div className="p-4 rounded-lg bg-slate-700/30">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-2xl">🇫🇷</span>
                      <div>
                        <h3 className="font-semibold text-white">France (EUR)</h3>
                        <p className="text-sm text-slate-400">European market</p>
                      </div>
                      <Badge className="ml-auto bg-blue-500/20 text-blue-400">Active</Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                      {mockPlans.map((plan, idx) => (
                        <div key={plan.id} className="text-center">
                          <p className="text-sm text-slate-400">{plan.name}</p>
                          <p className="text-lg font-semibold text-white">
                            €{[19, 49, 99, 299][idx]}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Global */}
                  <div className="p-4 rounded-lg bg-slate-700/30">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-2xl">🌍</span>
                      <div>
                        <h3 className="font-semibold text-white">Global (USD)</h3>
                        <p className="text-sm text-slate-400">Rest of world</p>
                      </div>
                      <Badge className="ml-auto bg-violet-500/20 text-violet-400">Active</Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                      {mockPlans.map((plan, idx) => (
                        <div key={plan.id} className="text-center">
                          <p className="text-sm text-slate-400">{plan.name}</p>
                          <p className="text-lg font-semibold text-white">
                            ${[19, 49, 99, 299][idx]}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="trials" className="mt-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Free Trial Configuration</CardTitle>
                <CardDescription className="text-slate-400">
                  Configure free trial settings for each plan
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {mockPlans.map((plan) => (
                    <div key={plan.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-700/30">
                      <div className="flex items-center gap-4">
                        <Badge className={`${colorClasses[plan.color].bg} ${colorClasses[plan.color].text}`}>
                          {plan.name}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <Label className="text-slate-400">Trial Days:</Label>
                          <Input
                            type="number"
                            defaultValue={14}
                            className="w-20 bg-slate-800 border-slate-600 text-white"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-slate-400">Credit Card Required:</Label>
                          <Switch />
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-slate-400">Enabled:</Label>
                          <Switch defaultChecked />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Plan Dialog */}
        <Dialog open={!!editingPlan} onOpenChange={() => setEditingPlan(null)}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Plan: {editingPlan?.name}</DialogTitle>
              <DialogDescription className="text-slate-400">
                Configure plan settings and features
              </DialogDescription>
            </DialogHeader>
            {editingPlan && (
              <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Plan Name</Label>
                    <Input defaultValue={editingPlan.name} className="bg-slate-800 border-slate-700" />
                  </div>
                  <div className="space-y-2">
                    <Label>Tier Level</Label>
                    <Input type="number" defaultValue={editingPlan.tierLevel} className="bg-slate-800 border-slate-700" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input defaultValue={editingPlan.description} className="bg-slate-800 border-slate-700" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Max Users</Label>
                    <Input type="number" defaultValue={editingPlan.maxUsers} className="bg-slate-800 border-slate-700" placeholder="-1 for unlimited" />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Invoices/Month</Label>
                    <Input type="number" defaultValue={editingPlan.maxInvoicesPerMonth} className="bg-slate-800 border-slate-700" placeholder="-1 for unlimited" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Monthly Price (DZD)</Label>
                    <Input type="number" defaultValue={editingPlan.priceMonthlyDzd} className="bg-slate-800 border-slate-700" />
                  </div>
                  <div className="space-y-2">
                    <Label>Yearly Price (DZD)</Label>
                    <Input type="number" defaultValue={editingPlan.priceYearlyDzd} className="bg-slate-800 border-slate-700" />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Included Features</Label>
                  <div className="grid grid-cols-2 gap-2 p-4 rounded-lg bg-slate-800">
                    {allFeatures.map((feature) => (
                      <div key={feature.code} className="flex items-center gap-2">
                        <Checkbox
                          id={feature.code}
                          defaultChecked={editingPlan.features.includes('all') || editingPlan.features.includes(feature.code)}
                        />
                        <Label htmlFor={feature.code} className="text-sm text-slate-300 cursor-pointer">
                          {feature.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800">
                  <div>
                    <p className="font-medium">Active</p>
                    <p className="text-sm text-slate-400">Plan is available for subscription</p>
                  </div>
                  <Switch defaultChecked={editingPlan.isActive} />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingPlan(null)} className="border-slate-700">
                Cancel
              </Button>
              <Button className="bg-violet-600 hover:bg-violet-700">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

export default AdminPlansPage;
